import React, { useEffect, useState } from 'react';
import { Table, Spinner, Badge } from 'react-bootstrap';
import axios from 'axios';

const Url = process.env.REACT_APP_MAIN_URL;

/**
 * Tournament-wide standings: pool play + bracket results combined.
 *
 * Separate from StandingsTable, which shows team_standing (pool play only,
 * the table that seeds the brackets).
 */
const GeneralStandingsTable = ({ divisionId, teamId, token, refreshTrigger }) => {
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (divisionId) fetchStandings();
  }, [divisionId, refreshTrigger]);

  const fetchStandings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${Url}api/general-standings/division/${divisionId}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      if (response.data.success) {
        setStandings(response.data.data || []);
      }
    } catch (error) {
      console.error('Error loading general standings:', error);
      setStandings([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading standings...</p>
      </div>
    );
  }

  if (standings.length === 0) {
    return (
      <div className="text-center py-5">
        <h5 className="text-muted">No standings available yet</h5>
        <p className="text-muted">
          Games need to be completed before tournament standings appear
        </p>
      </div>
    );
  }

  return (
    <div className="standings-table-container">
      <h4 className="mb-1 fw-bold">Tournament Standings</h4>
      <p className="text-muted" style={{ fontSize: '0.85rem' }}>
        All phases combined — pool play and bracket games. Bracket seeding still
        uses the Pool Play Standings tab.
      </p>

      <div style={{ overflowX: 'auto' }}>
        <Table striped bordered hover responsive>
          <thead className="bg-dark text-white">
            <tr>
              <th className="text-center">#</th>
              <th>Team</th>
              <th className="text-center">W</th>
              <th className="text-center">L</th>
              <th className="text-center">T</th>
              <th className="text-center">PCT</th>
              <th className="text-center">Pool</th>
              <th className="text-center">Bracket</th>
              <th className="text-center">RS</th>
              <th className="text-center">RA</th>
              <th className="text-center">DIFF</th>
            </tr>
          </thead>
          <tbody>
            {standings.map((s, index) => (
              <tr
                key={s.generalStandingId}
                className={teamId && s.teamId === teamId ? 'table-primary' : ''}
              >
                <td className="text-center">{index + 1}</td>
                <td>
                  {s.teamName}
                  {s.champion && (
                    <Badge bg="warning" text="dark" className="ms-2">
                      🏆 Champion
                    </Badge>
                  )}
                </td>
                <td className="text-center fw-bold">{s.wins}</td>
                <td className="text-center">{s.losses}</td>
                <td className="text-center">{s.ties}</td>
                <td className="text-center">{s.winningPercentage}</td>
                <td className="text-center text-muted">{s.poolRecord}</td>
                <td className="text-center text-muted">{s.bracketRecord}</td>
                <td className="text-center">{s.runsScored}</td>
                <td className="text-center">{s.runsAllowed}</td>
                <td className="text-center">
                  {s.runDifferential > 0 ? `+${s.runDifferential}` : s.runDifferential}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default GeneralStandingsTable;